import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';
import type { ILoadEditViewModel } from '@typeDefinitions/loadTypes';

import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  // Mutation bookkeeping.
  'operationType',

  // UI-only and derived values.
  'appointment',
  'availableServices',
  'canRate',
  'canVoidLoad',
  'canVoidOrder',
  'carrierContact',
  'coveredByEmail',
  'customerContact',
  'draftFormState',
  'driverIn',
  'driverOut',
  'ediUpdates',
  'editable',
  'forceCarrierChange',
  'forceCustomerChange',
  'isCarrierDirty',
  'lineItemsTotal',
  'loadInformation',
  'open',
  'operationsRep',
  'orderRep',
  'otherServiceType',
  'paymentsTotal',
  'quoteItems',
  'quoteStops',
  'readOnly',
  'shouldSendEdiBooleanVals',
  'stopDateFormatted',
  'stopDateTimeFormatted',
  'vendorPayDisplay',
  'warnings',
  'weightString',

  // Server-owned audit and live-status values.
  'aiTrackingStatus',
  'carrierStatus',
  'checkCallStatus',
  'createdBy',
  'createdDate',
  'lastKnownLocation',
  'modifiedBy',
  'modifiedDate',
  'nextCheckCallStatus',
  'pendingRateRequest',
  'secureRateConViewModels',
  'truckBids',
  'usersViewing',
]);

const ROOT_FORM_BY_FIELD: Partial<Record<string, ChangeTrackingSource>> = {
  adjustments: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  billingHold: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  chargeCreditCardFee: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  comments: CHANGE_TRACKING_SOURCES.orderDetails.internalNotes,
  currencyType: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  customerContactId: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  customerId: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  exceptionReason: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  flags: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  hideCustomerName: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  holdRetraction: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  lineItems: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  loadTenderId: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  metadata: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  operationsId: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  payments: CHANGE_TRACKING_SOURCES.orderDetails.customerPayments,
  pointOfSaleInstructions: CHANGE_TRACKING_SOURCES.orderDetails.pointOfSale,
  referenceNumber: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  revisitRetractionDate:
    CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  requiredTrackingService:
    CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  show214: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  status: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  trackingServiceName: CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
  trackingVisibilityProvider:
    CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
};

const VENDOR_FORM_BY_FIELD: Partial<Record<string, ChangeTrackingSource>> = {
  lineItems: CHANGE_TRACKING_SOURCES.orderDetails.vendorCosts,
  payments: CHANGE_TRACKING_SOURCES.orderDetails.vendorPayments,
};

const LOAD_FORM_BY_FIELD: Partial<Record<string, ChangeTrackingSource>> = {
  claim: CHANGE_TRACKING_SOURCES.orderDetails.claim,
  comments: CHANGE_TRACKING_SOURCES.orderDetails.tracking,
  items: CHANGE_TRACKING_SOURCES.orderDetails.stops,
  lineItems: CHANGE_TRACKING_SOURCES.orderDetails.carrierBilling,
  loadTracking: CHANGE_TRACKING_SOURCES.orderDetails.tracking,
  nextCheckCallDateUtc: CHANGE_TRACKING_SOURCES.orderDetails.tracking,
  payments: CHANGE_TRACKING_SOURCES.orderDetails.carrierBilling,
  practicalMileage: CHANGE_TRACKING_SOURCES.orderDetails.stops,
  shortestMileage: CHANGE_TRACKING_SOURCES.orderDetails.stops,
  stops: CHANGE_TRACKING_SOURCES.orderDetails.stops,
  useStatusStateMachine: CHANGE_TRACKING_SOURCES.orderDetails.stops,
};

const loadFieldSourceOverrides = new Map<
  keyof ILoadEditViewModel,
  ChangeTrackingSource
>();

const LOAD_BILLING_FIELDS = new Set([
  'billedDate',
  'billingHold',
  'billingHoldDescription',
  'billingHoldReason',
  'currencyType',
  'paidDate',
  'profitSplit',
]);

const LOAD_ASSIGNMENT_DISPLAY_FIELDS = new Map([
  ['assignedToUser', 'assignedToUserId'],
  ['capacityOpsRepUser', 'capacityOpsRepUserId'],
  ['coveredBy', 'coveredById'],
]);

const STOP_NON_USER_EDITABLE_FIELDS = new Set(['customerId', 'loadId', 'mode']);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const shouldIgnoreOrderPath = (
  path: readonly ChangedFieldPathToken[],
): boolean => {
  const properties = getPropertyPath(path);

  if (
    properties.length === 3 &&
    properties[0] === 'loads' &&
    properties[1] === 'stops' &&
    STOP_NON_USER_EDITABLE_FIELDS.has(properties[2])
  ) {
    return true;
  }

  return (
    properties.length === 3 &&
    properties[0] === 'loads' &&
    properties[1] === 'items' &&
    properties[2] === 'key'
  );
};

const isEmptyFormValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    !(value instanceof Date) &&
    Object.keys(value).length === 0);

const normalizeIsoDateTime = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;

  const match = value.match(
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/,
  );
  if (match === null) return null;

  return `${match[1]}T${match[2]}:${match[3] || '00'}`;
};

const areOrderScalarValuesEqual = (
  before: unknown,
  after: unknown,
  path: readonly ChangedFieldPathToken[],
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  if (before instanceof Date && after instanceof Date) {
    return before.getTime() === after.getTime();
  }

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  const beforeDateTime = normalizeIsoDateTime(before);
  const afterDateTime = normalizeIsoDateTime(after);
  if (beforeDateTime !== null && afterDateTime !== null) {
    return beforeDateTime === afterDateTime;
  }

  const field = getPropertyPath(path).at(-1) ?? '';
  if (
    /phone|contactNumber/i.test(field) &&
    typeof before === 'string' &&
    typeof after === 'string'
  ) {
    return before.replace(/\D/g, '') === after.replace(/\D/g, '');
  }

  return false;
};

const getLoadForPath = (
  order: IOrderViewModel,
  path: readonly ChangedFieldPathToken[],
) => {
  const loadToken = path.at(1);
  if (loadToken?.type !== 'item') return undefined;

  if (loadToken.id !== undefined) {
    return order.loads.find((load) => String(load.id) === String(loadToken.id));
  }

  return order.loads[loadToken.index];
};

const isAssignmentDisplayHydration = (
  beforeOrder: IOrderViewModel,
  afterOrder: IOrderViewModel,
  path: readonly ChangedFieldPathToken[],
): boolean => {
  const properties = getPropertyPath(path);
  if (properties.length !== 2 || properties[0] !== 'loads') return false;

  const identityField = LOAD_ASSIGNMENT_DISPLAY_FIELDS.get(properties[1]);
  if (identityField === undefined) return false;

  const beforeLoad = getLoadForPath(beforeOrder, path);
  const afterLoad = getLoadForPath(afterOrder, path);
  if (beforeLoad === undefined || afterLoad === undefined) return false;

  return areOrderScalarValuesEqual(
    beforeLoad[identityField as keyof typeof beforeLoad],
    afterLoad[identityField as keyof typeof afterLoad],
    path,
  );
};

const getOrderForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const properties = getPropertyPath(path);
  const rootField = properties.at(0);

  if (rootField === undefined)
    return CHANGE_TRACKING_SOURCES.orderDetails.orderHeader;

  if (rootField === 'vendors') {
    const vendorField = properties.at(1);
    return vendorField === undefined
      ? CHANGE_TRACKING_SOURCES.orderDetails.vendorDetails
      : (VENDOR_FORM_BY_FIELD[vendorField] ??
          CHANGE_TRACKING_SOURCES.orderDetails.vendorDetails);
  }

  if (rootField !== 'loads') {
    return (
      ROOT_FORM_BY_FIELD[rootField] ??
      CHANGE_TRACKING_SOURCES.orderDetails.customerInformation
    );
  }

  const loadField = properties.at(1);
  if (loadField === undefined)
    return CHANGE_TRACKING_SOURCES.orderDetails.loadDetails;
  const loadFieldOverride = loadFieldSourceOverrides.get(
    loadField as keyof ILoadEditViewModel,
  );
  if (loadFieldOverride !== undefined) {
    return loadFieldOverride;
  }
  if (LOAD_FORM_BY_FIELD[loadField] !== undefined) {
    return LOAD_FORM_BY_FIELD[loadField];
  }
  if (LOAD_BILLING_FIELDS.has(loadField)) {
    return CHANGE_TRACKING_SOURCES.orderDetails.carrierBilling;
  }
  return CHANGE_TRACKING_SOURCES.orderDetails.loadDetails;
};

const findOrderChangedForms = (
  before: IOrderViewModel,
  after: IOrderViewModel,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    shouldIgnorePath: shouldIgnoreOrderPath,
    areScalarValuesEqual: (beforeValue, afterValue, path) =>
      isAssignmentDisplayHydration(before, after, path) ||
      areOrderScalarValuesEqual(beforeValue, afterValue, path),
    getForm: (change) => getOrderForm(change.path),
  });

const orderFormChangeTracker = createFormChangeTracker<
  IOrderViewModel,
  ChangeTrackingSource
>({
  getIdentity: (order) => (order.id > 0 ? order.id : undefined),
  findChangedForms: findOrderChangedForms,
});

export const markOrderLoadFieldChangeSource = (
  field: keyof ILoadEditViewModel,
  source: ChangeTrackingSource,
) => {
  loadFieldSourceOverrides.set(field, source);
};

export const setOrderChangeBaseline = (order: IOrderViewModel) => {
  loadFieldSourceOverrides.clear();
  orderFormChangeTracker.setBaseline(order);
};

export const clearOrderChangeBaseline = () => {
  loadFieldSourceOverrides.clear();
  orderFormChangeTracker.clearBaseline();
};
export const synchronizeOrderChangeHydration =
  orderFormChangeTracker.synchronizeHydration;
export const prepareOrderChangeSummary = orderFormChangeTracker.prepare;
